import type { GivingInterval, LabelInValue } from 'models';
import { Col, Empty, EntitySnippet, Form, Icon, Row, Select } from 'ui';
import { images } from 'common/utils';
import { useUser } from 'features/auth';
import { OrganizationSelect } from 'features/organizations';
import { useSubscriptionPlans } from '../../../../../hooks';
import { useGiveNow } from '../../../context/hooks/useGiveNow';
import { GiveNowItemsWrapper } from '../give-now-items-wrapper';
import { GiveNowOptions } from './give-now-options';

type FormValues = { interval: GivingInterval; organization?: LabelInValue };

type P = {
  getPathToGivingDetail: (id: string) => string;
};

const GiveNowOptionsWithFilter = ({ getPathToGivingDetail }: P) => {
  const { railsUserData: viewer } = useUser();

  const { clearViewerItems, viewerItems } = useGiveNow();
  const { options } = useSubscriptionPlans();

  const hasOnlyOneOrganization = viewer.membershipsCount.joinedOrganizations === 1;
  const viewerOrganization = viewer.memberships.find((membership) => membership.subjectType === 'Organization');
  const initialValues = {
    interval: viewerItems[0]?.recurring ?? 'one_time',
    organization:
      viewerItems[0]?.organization ??
      (hasOnlyOneOrganization ? { label: '', value: viewerOrganization?.subjectId } : undefined),
  };
  const onFormItemSelected = () => {
    clearViewerItems();
  };

  return (
    <GiveNowItemsWrapper>
      <Form<FormValues> initialValues={initialValues}>
        {hasOnlyOneOrganization ? (
          <Form.Item<FormValues>
            className="give-now-organization-form-item"
            label="Recurring"
            name="interval"
            rules={[{ required: true }]}
          >
            <Select onSelect={onFormItemSelected} options={options} />
          </Form.Item>
        ) : (
          <Row className="give-now-organization-form-item" gutter={['ms', 0]}>
            <Col span={12}>
              <Form.Item<FormValues> label="Recurring" name="interval" rules={[{ required: true }]}>
                <Select onSelect={onFormItemSelected} options={options} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item<FormValues> label="Organization" name="organization" rules={[{ required: true }]}>
                <OrganizationSelect<LabelInValue>
                  createOption={(organization) => ({
                    label: (
                      <EntitySnippet
                        avatarProps={{
                          fallbackPhoto: images.default.organizationProfilePhoto,
                          size: 'sm',
                          src: organization.squareLogo ?? organization.profilePhoto,
                        }}
                        titleProps={{ children: organization.name }}
                      />
                    ),
                    value: organization.id,
                  })}
                  onSelect={onFormItemSelected}
                  query={{ filter: { scope: 'joined' } }}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Form.Item<FormValues> dependencies={['organization', 'interval']} noStyle>
          {({ getFieldsValue }) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
            const { interval, organization }: Partial<FormValues> = getFieldsValue(true);

            return interval && organization?.value ? (
              <GiveNowOptions
                getPathToGivingDetail={getPathToGivingDetail}
                hasOnlyOneOrganization={hasOnlyOneOrganization}
                interval={interval}
                organizationId={organization.value}
              />
            ) : (
              <Empty
                description={{
                  subtitle:
                    'You are a member of multiple organizations. Please select an organization to proceed with Give Now',
                  title: 'Select organization',
                }}
                image={<Icon icon="bank-line" />}
              />
            );
          }}
        </Form.Item>
      </Form>
    </GiveNowItemsWrapper>
  );
};

export default GiveNowOptionsWithFilter;
